import { useState } from "react";
import { Link } from "react-router-dom";
import UseItemOverlay from "./UseItemOverlay";
import { formatCount } from "../utils/formatCount";

const STROKE = 1.8;

const ITEM_STYLES = {
  boost_creator: { bg: "#FDF3E0", stroke: "#C4790A", label: "Boost Creator" },
  drop_alert: { bg: "#FFF0E6", stroke: "#C4790A", label: "Drop Alert" },
  super_comment: { bg: "#F0F0F0", stroke: "#9A9088", label: "Super Comment" },
  credit_tip_small: { bg: "#FDF3E0", stroke: "#C4790A", label: "Credit Tip — Small" },
  credit_tip_large: { bg: "#F0F0F0", stroke: "#9A9088", label: "Credit Tip — Large" },
  wall_note: { bg: "#E4F5EE", stroke: "#1A8C52", label: "Appreciation Wall Note" },
  theme_dusk: { bg: "#FDF3E0", stroke: "#C4790A", label: "Soft Dusk Theme" },
  theme_forest: { bg: "#E4F5EE", stroke: "#1A8C52", label: "Forest Theme" },
  theme_golden_hour: { bg: "#FFF0E6", stroke: "#C4790A", label: "Golden Hour Theme" },
  theme_midnight: { bg: "#F0F0F0", stroke: "#9A9088", label: "Midnight Theme" },
};

function ItemIcon({ itemId, stroke, size = 18 }) {
  const icons = {
    boost_creator: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    ),
    drop_alert: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
    wall_note: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
    default: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    ),
  };
  return icons[itemId] || icons.default;
}

function formatDate(ts) {
  if (!ts) return "";
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return "Purchased today";
  if (diffDays === 1) return "Purchased yesterday";
  if (diffDays < 7) return `Purchased ${diffDays} days ago`;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

function formatRelative(ts) {
  if (!ts) return "Recently purchased";
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return "Purchased today";
  if (diffDays === 1) return "Purchased 1 day ago";
  if (diffDays < 7) return `Purchased ${diffDays} days ago`;
  if (diffDays < 14) return "Purchased 1 week ago";
  return `Purchased ${formatDate(ts)}`;
}

export default function PerksInventory({ purchases = [], onBrowseStore }) {
  const [activeOverlay, setActiveOverlay] = useState(null);

  if (purchases.length === 0) {
    return (
      <div className="pk-items-empty">
        <div className="pk-items-empty-icon">
          <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#9A9088" strokeWidth={STROKE} strokeLinecap="round" aria-hidden="true">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
          </svg>
        </div>
        <h3>Nothing here yet</h3>
        <p>Head to the Credit Store to spend your credits on boosts, notes, and more.</p>
        <div className="pk-items-empty-hand">your first purchase is always the most exciting</div>
        {onBrowseStore && (
          <button type="button" className="pk-st-btn pk-st-btn--can" style={{ marginTop: 20, maxWidth: 220, marginLeft: "auto", marginRight: "auto" }} onClick={onBrowseStore}>
            Browse Credit Store
          </button>
        )}
      </div>
    );
  }

  const sorted = [...purchases].sort((a, b) => {
    const aT = a.purchasedAt?.seconds || 0;
    const bT = b.purchasedAt?.seconds || 0;
    return bT - aT;
  });

  return (
    <>
      <div className="pk-items-list">
        {sorted.map((item) => {
          const meta = ITEM_STYLES[item.itemId] || {
            bg: "#FDF3E0",
            stroke: "#C4790A",
            label: item.description || item.itemId,
          };
          const isTheme = item.itemId?.startsWith("theme_");

          return (
            <div key={item.id} className="pk-item-row">
              <div className="pk-item-icon" style={{ background: meta.bg }}>
                <ItemIcon itemId={item.itemId} stroke={meta.stroke} />
              </div>
              <div className="pk-item-body">
                <strong>{meta.label}</strong>
                <span>{formatRelative(item.purchasedAt)}</span>
              </div>
              <div className="pk-item-cost">−{formatCount(Math.abs(item.amount))} ✦</div>
              {!isTheme && (
                <button
                  type="button"
                  className="pk-st-btn pk-st-btn--can"
                  style={{ width: "auto", padding: "8px 14px", flexShrink: 0 }}
                  onClick={() =>
                    setActiveOverlay({ itemId: item.itemId, itemName: meta.label })
                  }
                >
                  Use
                </button>
              )}
              {isTheme && (
                <Link
                  to="/perks"
                  className="pk-st-btn pk-st-btn--can"
                  style={{ width: "auto", padding: "8px 14px", flexShrink: 0, textDecoration: "none" }}
                  onClick={() => {
                    document.querySelector('[data-tab="appLook"]')?.click();
                  }}
                >
                  Apply
                </Link>
              )}
            </div>
          );
        })}
      </div>

      {activeOverlay && (
        <UseItemOverlay
          itemId={activeOverlay.itemId}
          itemName={activeOverlay.itemName}
          onClose={() => setActiveOverlay(null)}
        />
      )}
    </>
  );
}
